"use client";

import { Pencil, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Skeleton } from "@/components/ds/skeleton";
import { Card } from "@/components/ui-system";
import { useDashboardBuilderOptional } from "@/contexts/DashboardBuilderContext";

import type { DashboardWidgetDto } from "@/lib/api/types";

type WidgetChromeProps = {
  widget: DashboardWidgetDto;
  title: string;
  subtitle?: string;
  isLoading: boolean;
  error: Error | null;
  children: ReactNode;
  /** When set, shows a link to create a batch alert rule prefilled from this widget. */
  createAlertHref?: string | null;
  headerActions?: ReactNode;
  /** Highlight card when GET /alerts/summary/active includes this widget's binding fingerprint. */
  hasActiveAlert?: boolean;
};

export function WidgetChrome({
  widget,
  title,
  subtitle,
  isLoading,
  error,
  children,
  headerActions,
  hasActiveAlert,
}: WidgetChromeProps) {
  const builder = useDashboardBuilderOptional();
  const [hover, setHover] = useState(false);
  const editMode = Boolean(builder?.editMode);
  const selected = builder?.selectedWidgetId === widget.id;
  const showBuilder = editMode && (selected || hover);

  return (
    <Card
      className={`group/chrome relative flex h-full min-h-[10rem] flex-col border-border/60 bg-card/95 p-ds-4 shadow-card ${
        hasActiveAlert ? "ring-2 ring-error/55 ring-offset-2 ring-offset-background" : ""
      }`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <header
        className={`relative mb-ds-3 flex items-start justify-between gap-ds-2 border-b border-border/50 pb-ds-3 after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-border-strong/25 after:to-transparent ${editMode ? "cursor-pointer" : ""}`}
        onClick={(e) => {
          if (!editMode || !builder) {
            return;
          }
          if ((e.target as HTMLElement).closest("button")) {
            return;
          }
          builder.setSelectedWidgetId(widget.id);
        }}
      >
        <div className="min-w-0 flex-1">
          <h3 className="text-caption font-semibold text-foreground">{title}</h3>
          <p className="mt-ds-1 text-micro font-medium uppercase tracking-[0.12em] text-kicker-lux">
            {subtitle ?? widget.type.replace(/_/g, " ")}
          </p>
        </div>
        {headerActions || showBuilder ? (
          <div className="flex shrink-0 gap-ds-1">
            {headerActions}
            <button
              type="button"
              className={`rounded-lg border border-border/50 bg-card p-ds-1 text-muted hover:border-border hover:text-foreground ${showBuilder ? "" : "hidden"}`}
              aria-label="Edit configuration"
              onClick={(e) => {
                e.stopPropagation();
                builder?.setSelectedWidgetId(widget.id);
              }}
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              className={`rounded-lg border border-border/50 bg-card p-ds-1 text-muted hover:border-error/40 hover:text-error ${showBuilder ? "" : "hidden"}`}
              aria-label="Delete widget"
              onClick={(e) => {
                e.stopPropagation();
                void builder?.deleteWidget(widget.id);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        ) : null}
      </header>
      <div className="flex min-h-0 flex-1 flex-col">
        {error ? (
          <p className="rounded-xl border border-error/30 bg-error/5 px-ds-3 py-ds-2 text-caption text-error" role="alert">
            {error.message}
          </p>
        ) : isLoading ? (
          <div className="flex flex-1 flex-col justify-center gap-ds-2 py-ds-2">
            <Skeleton className="h-4 w-[60%] max-w-[12rem]" />
            <Skeleton className="h-8 w-2/5 max-w-[8rem]" />
            <Skeleton className="h-16 w-full max-w-full rounded-xl" />
          </div>
        ) : (
          children
        )}
      </div>
    </Card>
  );
}
